import { useState, useCallback } from 'react';
import { openPrintWindow } from '@/lib/print-document';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import {
  Calculator, BookOpen, FileText, Scale, TrendingUp,
  ArrowUpRight, ArrowDownRight,
  Plus, Trash2, Download, Printer,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { notify } from '@/lib/toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/ui/stat-card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { TabBar } from '@/components/ui/tab-bar';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { formatCurrency, formatDateOnly, humanizeAccountName, humanizeEnum } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { RecordLink } from '@/components/records/RecordLink';
import { useRecordViewer } from '@/components/records/RecordViewer';
import { useChartColors } from '@/lib/chart-colors';

const tooltipFmt = (value: unknown) => formatCurrency(Number(value ?? 0));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type Tab = 'overview' | 'chart' | 'journal' | 'trial-balance' | 'pnl' | 'balance-sheet' | 'cash-flow';

interface AccountNode {
  id: string;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
  subType?: string;
  children?: AccountNode[];
}

interface TrialBalanceAccount {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  accountSubType?: string;
  totalDebit: number;
  totalCredit: number;
  balance: number;
}

interface TrialBalanceResponse {
  asOfDate: string;
  accounts: TrialBalanceAccount[];
  totals: {
    totalDebit: number;
    totalCredit: number;
    isBalanced: boolean;
    difference: number;
  };
}

interface ReportAccount {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  accountSubType?: string;
  totalDebit: number;
  totalCredit: number;
  balance: number;
}

interface AccountSection {
  accounts: ReportAccount[];
  total: number;
}

interface PnlResponse {
  period: { startDate: string; endDate: string };
  revenue: AccountSection;
  costOfGoods: AccountSection;
  grossProfit: number;
  operatingExpenses: AccountSection;
  netIncome: number;
}

interface BalanceSheetResponse {
  asOfDate: string;
  assets: AccountSection;
  liabilities: AccountSection;
  equity: AccountSection & { retainedEarnings: number };
  totals: {
    totalAssets: number;
    totalLiabilitiesAndEquity: number;
    isBalanced: boolean;
  };
}

interface JournalLine {
  id: string;
  debitAmount: string;
  creditAmount: string;
  account: { id: string; code: string; name: string };
}

interface JournalEntry {
  id: string;
  entryNumber: string;
  date: string;
  description: string;
  status: string;
  referenceType?: string;
  referenceId?: string;
  /** POS-I244: the Reversal entry of a reversed stock adjustment cannot be voided. */
  voidBlocked?: boolean;
  lines: JournalLine[];
}

interface CashFlowItem {
  description: string;
  amount: number;
}

/** Shape of GET /reports/cash-flow (report.service.ts getCashFlow). */
interface CashFlowResponse {
  period: { startDate: string; endDate: string };
  operating: {
    netIncome: number;
    adjustments: Record<string, number>;
    netCashFromOperating: number;
  };
  investing: { items: CashFlowItem[]; netCashFromInvesting: number };
  financing: { items: CashFlowItem[]; netCashFromFinancing: number };
  summary: { openingCash: number; netChange: number; closingCash: number };
}

/** Body of POST /journal-entries as built by handleJournalSave. */
interface JournalEntryPayload {
  date: string;
  description: string;
  lines: Array<{ accountId: string; description: string; debitAmount: number; creditAmount: number }>;
}

type CsvCell = string | number | null | undefined;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function downloadCSV(filename: string, headers: string[], rows: CsvCell[][]) {
  const escape = (v: CsvCell) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csvContent = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function printReport(title: string, contentHtml: string) {
  // POS-I240: one shared builder (base style, escaped title, Powered by footer on every page).
  openPrintWindow({
    title,
    heading: title,
    subtitle: `Generated on ${new Date().toLocaleDateString()}`,
    bodyHtml: contentHtml,
    extraCss: '.subtitle{font-size:12px;color:#666;margin-bottom:16px;}',
  });
}

function flattenAccounts(nodes: AccountNode[]): AccountNode[] {
  return nodes.reduce((acc: AccountNode[], a) => {
    acc.push(a);
    if (a.children?.length) acc.push(...flattenAccounts(a.children));
    return acc;
  }, []);
}

const emptyLine = { accountId: '', description: '', debitAmount: 0, creditAmount: 0 };

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export function AccountingPage() {
  const { t } = useTranslation('finance');
  // POS-I146: deep link such as /finance/accounting?tab=trial-balance
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tab, setTab] = useState<Tab>(
    (['overview', 'chart', 'journal', 'trial-balance', 'pnl', 'balance-sheet', 'cash-flow'] as const).find(x => x === tabParam) ?? 'overview',
  );
  const { hasPermission } = useAuthStore();
  const { open: openRecord } = useRecordViewer();
  // POS-I238: the whole card opens the journal entry in the record viewer; focus returns to the entry number.
  const openJournalEntry = (card: HTMLElement, je: { id: string; entryNumber: string }) => {
    if (!hasPermission('accounting:read')) return;
    openRecord(
      { type: 'journalEntry', id: je.id, label: je.entryNumber },
      { returnFocusTo: card.querySelector<HTMLElement>('[data-record-link]') ?? card },
    );
  };
  const { series, seriesAt, grid, axis, status } = useChartColors();
  const canCreate = hasPermission('accounting:create') || hasPermission('accounting:*');
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [confirmPost, setConfirmPost] = useState(false);
  const canUpdate = hasPermission('accounting:update') || hasPermission('accounting:*');
  const canVoid = hasPermission('accounting:delete') || hasPermission('accounting:*');
  const [voidTarget, setVoidTarget] = useState<JournalEntry | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [journalForm, setJournalForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    lines: [{ ...emptyLine }, { ...emptyLine }] as Array<{ accountId: string; description: string; debitAmount: number; creditAmount: number }>,
  });
  const [pnlStartDate, setPnlStartDate] = useState('');
  const [pnlEndDate, setPnlEndDate] = useState('');
  const [jePage, setJePage] = useState(1);

  const queryClient = useQueryClient();
  const handleAccountingUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['trial-balance'] });
    queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
    queryClient.invalidateQueries({ queryKey: ['profit-loss'] });
    queryClient.invalidateQueries({ queryKey: ['balance-sheet'] });
  }, [queryClient]);
  useSocketEvent('sale.created', handleAccountingUpdate);
  useSocketEvent('sale.voided', handleAccountingUpdate);
  useSocketEvent('purchase.received', handleAccountingUpdate);
  useSocketEvent('expense.created', handleAccountingUpdate);

  // --- Data Queries ---
  const { data: trialBalance, isLoading: trialBalanceLoading } = useQuery<TrialBalanceResponse>({
    queryKey: ['trial-balance'],
    queryFn: async () => (await api.get('/reports/trial-balance')).data,
  });

  // POS-I440: a reversed From/To range is never sent: the P&L tab shows a message and the Overview
  // Net Income card keeps using the default period instead of silently showing Rs 0.
  const pnlRangeInvalid = !!pnlStartDate && !!pnlEndDate && pnlStartDate > pnlEndDate;
  const pnlQueryParams = new URLSearchParams();
  if (pnlStartDate && !pnlRangeInvalid) pnlQueryParams.set('startDate', pnlStartDate);
  if (pnlEndDate && !pnlRangeInvalid) pnlQueryParams.set('endDate', pnlEndDate);
  const pnlQs = pnlQueryParams.toString();

  const { data: pnl } = useQuery<PnlResponse>({
    queryKey: ['profit-loss', pnlRangeInvalid ? '' : pnlStartDate, pnlRangeInvalid ? '' : pnlEndDate],
    queryFn: async () => (await api.get(`/reports/profit-loss${pnlQs ? `?${pnlQs}` : ''}`)).data,
  });

  const { data: balanceSheet } = useQuery<BalanceSheetResponse>({
    queryKey: ['balance-sheet'],
    queryFn: async () => (await api.get('/reports/balance-sheet')).data,
  });

  const [cfStartDate, setCfStartDate] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [cfEndDate, setCfEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const cfRangeInvalid = !!cfStartDate && !!cfEndDate && cfStartDate > cfEndDate;
  const cfQs = new URLSearchParams({ startDate: cfStartDate, endDate: cfEndDate }).toString();
  const { data: cashFlow, isLoading: cfLoading } = useQuery<CashFlowResponse>({
    queryKey: ['cash-flow', cfStartDate, cfEndDate],
    queryFn: async () => (await api.get(`/reports/cash-flow?${cfQs}`)).data,
    enabled: tab === 'cash-flow' && !cfRangeInvalid,
  });

  const { data: accounts } = useQuery<AccountNode[]>({
    queryKey: ['chart-of-accounts'],
    queryFn: async () => (await api.get('/accounts/tree')).data,
  });

  const { data: journals, isLoading: journalEntriesLoading } = useQuery<{ data: JournalEntry[]; meta: { total: number; page: number; limit: number } }>({
    queryKey: ['journal-entries', jePage],
    queryFn: async () => (await api.get(`/journal-entries?limit=20&page=${jePage}`)).data,
  });

  const flatAccts = accounts ? flattenAccounts(accounts) : [];

  // --- Journal Mutation ---
  const createJournalEntry = useMutation({
    // POS-I162: "Post Entry" creates the DRAFT and then posts it; "Save as Draft" only creates it.
    mutationFn: async ({ payload, post }: { payload: JournalEntryPayload; post: boolean }) => {
      const created = (await api.post('/journal-entries', payload)).data;
      if (post) {
        const id = (created?.data ?? created)?.id;
        await api.patch(`/journal-entries/${id}/post`);
      }
      return created;
    },
    onSuccess: (_data, vars) => {
      toast.success(vars.post ? t('journalEntryPosted') : t('entryDraftSaved'));
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['trial-balance'] });
      queryClient.invalidateQueries({ queryKey: ['profit-loss'] });
      queryClient.invalidateQueries({ queryKey: ['balance-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
      setJournalDialogOpen(false);
      setJournalForm({ date: new Date().toISOString().split('T')[0], description: '', lines: [{ ...emptyLine }, { ...emptyLine }] });
    },
    onError: (err: unknown) => {
      notify.error(err, t('journalEntryFailed'));
    },
  });

  const postJournalEntry = useMutation({
    mutationFn: async (id: string) => (await api.patch(`/journal-entries/${id}/post`)).data,
    onSuccess: () => {
      toast.success(t('journalEntryPosted'));
      invalidateLedger();
    },
    onError: (err: unknown) => notify.error(err, t('journalEntryFailed')),
  });

  const voidJournalEntry = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.patch(`/journal-entries/${id}/void`, { reason })).data,
    onSuccess: () => {
      toast.success(t('journalEntryVoided'));
      setVoidTarget(null);
      setVoidReason('');
      invalidateLedger();
    },
    onError: (err: unknown) => notify.error(err, t('journalEntryFailed')),
  });

  function invalidateLedger() {
    queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
    queryClient.invalidateQueries({ queryKey: ['trial-balance'] });
    queryClient.invalidateQueries({ queryKey: ['profit-loss'] });
    queryClient.invalidateQueries({ queryKey: ['balance-sheet'] });
    queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
  }

  function handleJournalSave(post: boolean) {
    if (!journalForm.description.trim()) { toast.error(t('descriptionRequired')); return; }
    const validLines = journalForm.lines.filter((l) => l.accountId);
    if (validLines.length < 2) { toast.error(t('atLeast2Lines')); return; }
    for (const line of validLines) {
      if (line.debitAmount > 0 && line.creditAmount > 0) { toast.error(t('lineCannotHaveBoth')); return; }
      if (line.debitAmount === 0 && line.creditAmount === 0) { toast.error(t('lineNeedsAmount')); return; }
    }
    const totalDebit = validLines.reduce((s, l) => s + Number(l.debitAmount), 0);
    const totalCredit = validLines.reduce((s, l) => s + Number(l.creditAmount), 0);
    if (totalDebit <= 0 || totalCredit <= 0) { toast.error(t('debitsCreditsGtZero')); return; }
    if (Math.abs(totalDebit - totalCredit) > 0.001) { toast.error(t('debitsEqualCredits')); return; }
    createJournalEntry.mutate({
      post,
      payload: {
        date: journalForm.date,
        description: journalForm.description,
        lines: validLines.map((l) => ({ accountId: l.accountId, description: l.description, debitAmount: l.debitAmount || 0, creditAmount: l.creditAmount || 0 })),
      },
    });
  }

  // --- Export / Print Helpers ---
  function exportTrialBalanceCSV() {
    if (!trialBalance?.accounts?.length) return;
    downloadCSV(
      'trial-balance.csv',
      ['Code', 'Account', 'Type', 'Debit', 'Credit', 'Balance'],
      trialBalance.accounts.map((a) => [a.accountCode, a.accountName, a.accountType, a.totalDebit, a.totalCredit, a.balance]),
    );
  }

  function printTrialBalance() {
    if (!trialBalance?.accounts?.length) return;
    const rows = trialBalance.accounts
      .map((a) => `<tr><td class="font-mono">${a.accountCode}</td><td>${a.accountName}</td><td class="text-end font-mono">${formatCurrency(a.totalDebit)}</td><td class="text-end font-mono">${formatCurrency(a.totalCredit)}</td></tr>`)
      .join('');
    printReport(
      'Trial Balance',
      `<table><thead><tr><th>Code</th><th>Account</th><th class="text-end">Debit</th><th class="text-end">Credit</th></tr></thead>
       <tbody>${rows}</tbody>
       <tfoot><tr class="total-row"><td colspan="2">Totals</td><td class="text-end font-mono">${formatCurrency(trialBalance.totals.totalDebit)}</td><td class="text-end font-mono">${formatCurrency(trialBalance.totals.totalCredit)}</td></tr></tfoot></table>`,
    );
  }

  function exportPnlCSV() {
    if (!pnl) return;
    const rows: CsvCell[][] = [];
    rows.push(['--- Revenue ---', '', '', '']);
    pnl.revenue.accounts.forEach((a) => rows.push([a.accountCode, a.accountName, a.balance, '']));
    rows.push(['Total Revenue', '', pnl.revenue.total, '']);
    rows.push(['--- Cost of Goods ---', '', '', '']);
    pnl.costOfGoods.accounts.forEach((a) => rows.push([a.accountCode, a.accountName, '', a.balance]));
    rows.push(['Total COGS', '', '', pnl.costOfGoods.total]);
    rows.push(['Gross Profit', '', pnl.grossProfit, '']);
    rows.push(['--- Operating Expenses ---', '', '', '']);
    pnl.operatingExpenses.accounts.forEach((a) => rows.push([a.accountCode, a.accountName, '', a.balance]));
    rows.push(['Total Expenses', '', '', pnl.operatingExpenses.total]);
    rows.push(['Net Income', '', pnl.netIncome, '']);
    downloadCSV('profit-loss.csv', ['Code', 'Account', 'Income', 'Expense'], rows);
  }

  function printPnl() {
    if (!pnl) return;
    const section = (sTitle: string, accts: ReportAccount[], total: number) => {
      const rows = accts
        .map((a) => `<tr><td class="font-mono">${a.accountCode}</td><td>${a.accountName}</td><td class="text-end font-mono">${formatCurrency(a.balance)}</td></tr>`)
        .join('');
      return `<div class="section-title">${sTitle}</div><table><tbody>${rows}</tbody><tfoot><tr class="total-row"><td colspan="2">Total</td><td class="text-end font-mono">${formatCurrency(total)}</td></tr></tfoot></table>`;
    };
    printReport(
      'Profit & Loss Statement',
      `${section('Revenue', pnl.revenue.accounts, pnl.revenue.total)}
       ${section('Cost of Goods Sold', pnl.costOfGoods.accounts, pnl.costOfGoods.total)}
       <div class="section-title">Gross Profit: ${formatCurrency(pnl.grossProfit)}</div>
       ${section('Operating Expenses', pnl.operatingExpenses.accounts, pnl.operatingExpenses.total)}
       <div class="section-title" style="font-size:16px;margin-top:20px;">Net Income: ${formatCurrency(pnl.netIncome)}</div>`,
    );
  }

  function exportBalanceSheetCSV() {
    if (!balanceSheet) return;
    const rows: CsvCell[][] = [];
    rows.push(['--- Assets ---', '', '']);
    balanceSheet.assets.accounts.forEach((a) => rows.push([a.accountCode, a.accountName, a.balance]));
    rows.push(['Total Assets', '', balanceSheet.assets.total]);
    rows.push(['--- Liabilities ---', '', '']);
    balanceSheet.liabilities.accounts.forEach((a) => rows.push([a.accountCode, a.accountName, a.balance]));
    rows.push(['Total Liabilities', '', balanceSheet.liabilities.total]);
    rows.push(['--- Equity ---', '', '']);
    balanceSheet.equity.accounts.forEach((a) => rows.push([a.accountCode, a.accountName, a.balance]));
    rows.push(['Retained Earnings', '', balanceSheet.equity.retainedEarnings]);
    rows.push(['Total Equity', '', balanceSheet.equity.total]);
    downloadCSV('balance-sheet.csv', ['Code', 'Account', 'Amount'], rows);
  }

  function printBalanceSheet() {
    if (!balanceSheet) return;
    const section = (sTitle: string, accts: ReportAccount[], total: number) => {
      const rows = accts
        .map((a) => `<tr><td class="font-mono">${a.accountCode}</td><td>${a.accountName}</td><td class="text-end font-mono">${formatCurrency(a.balance)}</td></tr>`)
        .join('');
      return `<div class="section-title">${sTitle}</div><table><tbody>${rows}</tbody><tfoot><tr class="total-row"><td colspan="2">Total ${sTitle}</td><td class="text-end font-mono">${formatCurrency(total)}</td></tr></tfoot></table>`;
    };
    printReport(
      'Balance Sheet',
      `${section('Assets', balanceSheet.assets.accounts, balanceSheet.assets.total)}
       ${section('Liabilities', balanceSheet.liabilities.accounts, balanceSheet.liabilities.total)}
       ${section('Equity', balanceSheet.equity.accounts, balanceSheet.equity.total)}`,
    );
  }

  // --- Chart Data ---
  const tbChartData = (trialBalance?.accounts || []).map((a) => ({
    name: a.accountCode,
    debit: a.totalDebit,
    credit: a.totalCredit,
  }));

  const revenuePieData = (pnl?.revenue.accounts || [])
    .filter((a) => a.balance > 0)
    .map((a) => ({ name: humanizeAccountName(a.accountName), value: a.balance }));
  const expenseBarData = (pnl?.operatingExpenses.accounts || []).map((a) => ({
    name: humanizeAccountName(a.accountName),
    value: Math.abs(a.balance),
  }));
  const pnlSummaryData = pnl
    ? [
        { name: t('revenue'), value: pnl.revenue.total },
        { name: t('cogs'), value: pnl.costOfGoods.total },
        { name: t('grossProfit'), value: pnl.grossProfit },
        { name: t('expenses'), value: pnl.operatingExpenses.total },
        { name: t('netIncome'), value: pnl.netIncome },
      ]
    : [];

  const assetPieData = (balanceSheet?.assets.accounts || []).map((a) => ({ name: a.accountName, value: a.balance }));
  const bsCompareData = balanceSheet
    ? [
        { name: t('assets'), value: balanceSheet.assets.total },
        { name: t('liabilities'), value: balanceSheet.liabilities.total },
        { name: t('equity'), value: balanceSheet.equity.total },
      ]
    : [];

  // --- Tabs Config ---
  const tabs = [
    { id: 'overview' as Tab, label: t('overview'), icon: Calculator },
    { id: 'chart' as Tab, label: t('accounts'), icon: BookOpen },
    { id: 'journal' as Tab, label: t('journalEntries'), icon: FileText },
    { id: 'trial-balance' as Tab, label: t('trialBalance'), icon: Scale },
    { id: 'pnl' as Tab, label: t('pnlTab'), icon: TrendingUp },
    { id: 'balance-sheet' as Tab, label: t('balanceSheet'), icon: Scale },
    { id: 'cash-flow' as Tab, label: t('cashFlow'), icon: TrendingUp },
  ];

  // --- Toolbar Buttons ---
  function ActionButtons({ onExport, onPrint }: { onExport: () => void; onPrint: () => void }) {
    return (
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="me-1 h-3.5 w-3.5" />
          {t('csv')}
        </Button>
        <Button variant="outline" size="sm" onClick={onPrint}>
          <Printer className="me-1 h-3.5 w-3.5" />
          {t('print')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('accountingTitle')}
        subtitle={t('doubleEntrySubtitle')}
        action={
          <Button variant="outline" size="sm" onClick={() => {
            const url = '/api/v1/journal-entries/export';
            const link = document.createElement('a');
            link.href = url;
            link.download = `journal-entries-${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
          }}>
            <Download className="h-4 w-4 me-1.5" />
            Export CSV
          </Button>
        }
      />

      {/* Tabs */}
      <div className="animate-fade-in-up -mx-1 border-b border-border/60 pb-1">
        <TabBar
          tabs={tabs.map((tabItem) => ({ key: tabItem.id, label: tabItem.label, icon: tabItem.icon }))}
          activeTab={tab}
          onTabChange={(key) => setTab(key as Tab)}
        />
      </div>

      {/* ======================= OVERVIEW ======================= */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard
            className="animate-fade-in-up stagger-1"
            label={t('trialBalance')}
            value={t('accountsCount', { count: trialBalance?.accounts?.length || 0 })}
            helper={
              trialBalance?.totals ? (
                <span className={trialBalance.totals.isBalanced ? 'text-success-text font-medium' : 'text-destructive-text font-medium'}>
                  {trialBalance.totals.isBalanced
                    ? `✓ ${t('balanced')}`
                    : `✗ ${t('diffValue', { amount: formatCurrency(trialBalance.totals.difference) })}`}
                </span>
              ) : undefined
            }
            icon={Scale}
            accent={trialBalance?.totals ? (trialBalance.totals.isBalanced ? 'success' : 'danger') : 'info'}
            onClick={() => setTab('trial-balance')}
          />
          <StatCard
            className="animate-fade-in-up stagger-2"
            label={t('netIncome')}
            value={
              <span className={(pnl?.netIncome ?? 0) >= 0 ? 'text-success-text' : 'text-destructive-text'}>
                {formatCurrency(pnl?.netIncome ?? 0)}
              </span>
            }
            icon={TrendingUp}
            accent={(pnl?.netIncome ?? 0) >= 0 ? 'success' : 'danger'}
            onClick={() => setTab('pnl')}
          />
          <StatCard
            className="animate-fade-in-up stagger-3"
            label={t('totalAssets')}
            value={balanceSheet?.totals?.totalAssets ?? 0}
            format="currency"
            icon={BookOpen}
            onClick={() => setTab('balance-sheet')}
          />
        </div>
      )}

      {/* ======================= CHART OF ACCOUNTS ======================= */}
      {tab === 'chart' && (
        <Card className="animate-fade-in-up">
          <CardHeader>
            <CardTitle className="text-lg">{t('accounts')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!accounts?.length ? (
              <p className="py-8 text-center text-muted-foreground">{t('noAccountsFound')}</p>
            ) : (
              <div className="space-y-1">
                {accounts.map((acc) => (
                  <AccountTreeItem key={acc.id} account={acc} depth={0} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ======================= JOURNAL ENTRIES ======================= */}
      {tab === 'journal' && (
        <Card className="animate-fade-in-up">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t('journalEntries')}</CardTitle>
              {canCreate && (
              <Button
                size="sm"
                className="bg-gradient-to-r from-primary via-primary to-primary text-primary-foreground shadow-md shadow-primary/20"
                onClick={() => setJournalDialogOpen(true)}
              >
                <Plus className="me-1 h-3.5 w-3.5" />
                {t('newEntry')}
              </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {journalEntriesLoading ? (
              <SkeletonTable rows={6} cols={5} />
            ) : !journals?.data?.length ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <BookOpen className="mb-3 h-12 w-12 text-muted-foreground/40" />
                <p className="text-sm font-medium text-muted-foreground">{t('noJournalEntries')}</p>
                <p className="mt-1 text-xs text-muted-foreground/70">{t('noJournalEntriesHint')}</p>
                {canCreate && (
                  <Button
                    size="sm"
                    className="mt-4 bg-gradient-to-r from-primary via-primary to-primary text-primary-foreground"
                    onClick={() => setJournalDialogOpen(true)}
                  >
                    <Plus className="me-1 h-3.5 w-3.5" />
                    {t('newEntry')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {journals.data.map((je) => (
                  <div
                    key={je.id}
                    role="button"
                    tabIndex={0}
                    data-testid="journal-entry-card"
                    onClick={(e) => openJournalEntry(e.currentTarget, je)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openJournalEntry(e.currentTarget, je);
                      }
                    }}
                    className="cursor-pointer rounded-lg border p-4 hover:bg-accent/50 transition-all duration-200 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm"><RecordLink type="journalEntry" id={je.id} label={je.entryNumber} variant="mono" /></p>
                        <p className="text-xs text-muted-foreground">{je.description}</p>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          je.status === 'POSTED'
                            ? 'bg-success/10 text-success-text'
                            : je.status === 'VOIDED'
                              ? 'bg-destructive/10 text-destructive-text'
                              : 'bg-muted text-foreground'
                        }`}
                      >
                        {je.status}
                      </span>
                    </div>
                    {((je.status === 'DRAFT' && canUpdate) || (je.status === 'POSTED' && canVoid && !je.voidBlocked)) && (
                      <div className="mt-2 flex justify-end gap-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        {je.status === 'DRAFT' && canUpdate && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={postJournalEntry.isPending}
                            onClick={() => postJournalEntry.mutate(je.id)}
                          >
                            {t('postEntry')}
                          </Button>
                        )}
                        {je.status === 'POSTED' && canVoid && !je.voidBlocked && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setVoidTarget(je); setVoidReason(''); }}
                          >
                            {t('voidEntry')}
                          </Button>
                        )}
                      </div>
                    )}
                    {je.status === 'POSTED' && je.voidBlocked && (
                      <p className="mt-2 text-xs text-muted-foreground" data-testid="void-blocked-note">{t('voidBlockedNote')}</p>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground">
                      {new Date(je.date).toLocaleDateString()} • {t('lineCount', { count: je.lines?.length || 0 })}
                      {je.referenceType && (
                        <>
                          {' '}
                          • <span className="font-medium">{humanizeAccountName(je.referenceType)}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {journals?.meta && (
              <div className="mt-4">
                <PaginationBar
                  page={jePage}
                  pageSize={20}
                  total={journals.meta.total}
                  onPageChange={setJePage}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ======================= TRIAL BALANCE ======================= */}
      {tab === 'trial-balance' && (
        <div className="space-y-4 animate-fade-in-up">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg">{t('trialBalance')}</CardTitle>
                  {trialBalance?.asOfDate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('asOf', { date: new Date(trialBalance.asOfDate).toLocaleDateString() })}
                    </p>
                  )}
                </div>
                <ActionButtons onExport={exportTrialBalanceCSV} onPrint={printTrialBalance} />
              </div>
            </CardHeader>
            <CardContent>
              {trialBalanceLoading ? (
                <SkeletonTable rows={8} cols={4} />
              ) : !trialBalance?.accounts?.length ? (
                <p className="py-8 text-center text-muted-foreground">
                  {t('noDataPostFirst')}
                </p>
              ) : (
                <div className="overflow-auto">
                  <Table mobileCards>
                    <THead>
                      <Tr hover={false}>
                        <Th>{t('code')}</Th>
                        <Th>{t('account')}</Th>
                        <Th>{t('type')}</Th>
                        <Th className="text-end">{t('debit')}</Th>
                        <Th className="text-end">{t('credit')}</Th>
                        <Th className="text-end">{t('balance')}</Th>
                      </Tr>
                    </THead>
                    <TBody>
                      {trialBalance.accounts.map((a) => (
                        <Tr
                          key={a.accountId}
                          className="duration-150"
                        >
                          <Td label={t('code')} className="font-mono text-xs">{a.accountCode}</Td>
                          <Td label={t('account')}>{a.accountName}</Td>
                          <Td label={t('type')} className="text-xs text-muted-foreground">{humanizeEnum(a.accountType)}</Td>
                          <Td label={t('debit')} className="text-end font-mono">
                            {a.totalDebit > 0 ? formatCurrency(a.totalDebit) : ''}
                          </Td>
                          <Td label={t('credit')} className="text-end font-mono">
                            {a.totalCredit > 0 ? formatCurrency(a.totalCredit) : ''}
                          </Td>
                          <Td label={t('balance')} className="text-end font-mono font-medium">
                            {formatCurrency(a.balance)}
                          </Td>
                        </Tr>
                      ))}
                      <Tr hover={false} className="!border-t-2 font-bold">
                        <Td colSpan={3}>
                          {t('totals')}
                        </Td>
                        <Td label={t('debit')} className="text-end font-mono">
                          {formatCurrency(trialBalance.totals.totalDebit)}
                        </Td>
                        <Td label={t('credit')} className="text-end font-mono">
                          {formatCurrency(trialBalance.totals.totalCredit)}
                        </Td>
                        <Td label={t('balance')} className="text-end">
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              trialBalance.totals.isBalanced
                                ? 'bg-success/10 text-success-text'
                                : 'bg-destructive/10 text-destructive-text'
                            }`}
                          >
                            {trialBalance.totals.isBalanced
                              ? `✓ ${t('balanced')}`
                              : t('diffValue', { amount: formatCurrency(trialBalance.totals.difference) })}
                          </span>
                        </Td>
                      </Tr>
                    </TBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trial Balance Chart */}
          {tbChartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('debitVsCredit')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={tbChartData}
                    margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis stroke={axis} dataKey="name" tick={{ fontSize: 11, fill: axis }} />
                    <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} />
                    <Tooltip formatter={tooltipFmt} />
                    <Legend />
                    <Bar dataKey="debit" name={t('debit')} fill={series[0]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="credit" name={t('credit')} fill={series[1]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ======================= PROFIT & LOSS ======================= */}
      {tab === 'pnl' && (
        <div className="space-y-4 animate-fade-in-up">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-lg">{t('pnlStatement')}</CardTitle>
                  {pnl?.period && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDateOnly(pnl.period.startDate)} — {formatDateOnly(pnl.period.endDate)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground">{t('from')}</Label>
                    <Input
                      type="date"
                      value={pnlStartDate}
                      onChange={(e) => setPnlStartDate(e.target.value)}
                      className="h-8 w-auto px-2 py-1"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground">{t('to')}</Label>
                    <Input
                      type="date"
                      value={pnlEndDate}
                      onChange={(e) => setPnlEndDate(e.target.value)}
                      className="h-8 w-auto px-2 py-1"
                    />
                  </div>
                  <ActionButtons onExport={exportPnlCSV} onPrint={printPnl} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {pnlRangeInvalid && (
                <p role="alert" data-testid="pnl-range-error" className="text-sm text-destructive-text">{t('dateRangeInvalid')}</p>
              )}
              <div className={pnlRangeInvalid ? 'hidden' : 'space-y-6'}>
                <ReportSection title={t('revenue')} accounts={pnl?.revenue.accounts || []} />
                <div className="border-t pt-2">
                  <div className="flex justify-between font-medium">
                    <span>{t('totalRevenue')}</span>
                    <span className="text-success-text">
                      {formatCurrency(pnl?.revenue.total ?? 0)}
                    </span>
                  </div>
                </div>
                <ReportSection
                  title={t('costOfGoodsSold')}
                  accounts={pnl?.costOfGoods.accounts || []}
                />
                <div className="border-t pt-2">
                  <div className="flex justify-between font-medium">
                    <span>{t('totalCogs')}</span>
                    <span className="text-warning-text">
                      {formatCurrency(pnl?.costOfGoods.total ?? 0)}
                    </span>
                  </div>
                </div>
                <div className="rounded-lg bg-gradient-to-r from-info/10 to-primary/10 p-3">
                  <div className="flex justify-between font-bold">
                    <span>{t('grossProfit')}</span>
                    <span>{formatCurrency(pnl?.grossProfit ?? 0)}</span>
                  </div>
                </div>
                <ReportSection
                  title={t('operatingExpenses')}
                  accounts={pnl?.operatingExpenses.accounts || []}
                />
                <div className="border-t pt-2">
                  <div className="flex justify-between font-medium">
                    <span>{t('totalOperatingExpenses')}</span>
                    <span className="text-destructive-text">
                      {formatCurrency(pnl?.operatingExpenses.total ?? 0)}
                    </span>
                  </div>
                </div>
                <div className="rounded-lg bg-gradient-to-r from-primary/10 to-primary/10 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold">{t('netIncome')}</span>
                    <div className="flex items-center gap-2">
                      {(pnl?.netIncome ?? 0) >= 0 ? (
                        <ArrowUpRight className="h-5 w-5 text-success-text" />
                      ) : (
                        <ArrowDownRight className="h-5 w-5 text-destructive-text" />
                      )}
                      <span
                        className={`text-xl font-bold ${
                          (pnl?.netIncome ?? 0) >= 0 ? 'text-success-text' : 'text-destructive-text'
                        }`}
                      >
                        {formatCurrency(pnl?.netIncome ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* P&L Charts */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {revenuePieData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('revenueBreakdown')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={revenuePieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                        }
                        labelLine={false}
                      >
                        {revenuePieData.map((_, i) => (
                          <Cell key={i} fill={seriesAt(i)} />
                        ))}
                      </Pie>
                      <Tooltip formatter={tooltipFmt} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            {expenseBarData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('expensesBreakdown')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={expenseBarData}
                      layout="vertical"
                      margin={{ top: 5, right: 20, bottom: 5, left: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                      <XAxis stroke={axis} type="number" tick={{ fontSize: 11, fill: axis }} />
                      <YAxis stroke={axis} dataKey="name" type="category" tick={{ fontSize: 11, fill: axis }} width={75} />
                      <Tooltip formatter={tooltipFmt} />
                      <Bar dataKey="value" name={t('amount')} fill={series[0]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
          {pnlSummaryData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('pnlSummary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={pnlSummaryData}
                    margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis stroke={axis} dataKey="name" tick={{ fontSize: 12, fill: axis }} />
                    <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} />
                    <Tooltip formatter={tooltipFmt} />
                    <Bar dataKey="value" name={t('amount')} radius={[4, 4, 0, 0]}>
                      {pnlSummaryData.map((entry, i) => (
                        <Cell key={i} fill={entry.value >= 0 ? status.success : status.destructive} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ======================= BALANCE SHEET ======================= */}
      {tab === 'balance-sheet' && (
        <div className="space-y-4 animate-fade-in-up">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-bold">{t('balanceSheet')}</h2>
              {balanceSheet?.asOfDate && (
                <p className="text-xs text-muted-foreground">
                  {t('asOf', { date: new Date(balanceSheet.asOfDate).toLocaleDateString() })}
                </p>
              )}
            </div>
            <ActionButtons onExport={exportBalanceSheetCSV} onPrint={printBalanceSheet} />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('assets')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ReportSection title="" accounts={balanceSheet?.assets.accounts || []} />
                <div className="mt-4 rounded-lg bg-primary/10 p-3">
                  <div className="flex justify-between font-bold">
                    <span>{t('totalAssets')}</span>
                    <span>{formatCurrency(balanceSheet?.assets.total ?? 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('liabilities')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ReportSection
                    title=""
                    accounts={balanceSheet?.liabilities.accounts || []}
                  />
                  <div className="mt-4 rounded-lg bg-warning/10 p-3">
                    <div className="flex justify-between font-bold">
                      <span>{t('totalLiabilities')}</span>
                      <span>{formatCurrency(balanceSheet?.liabilities.total ?? 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('equity')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ReportSection
                    title=""
                    accounts={balanceSheet?.equity.accounts || []}
                    hideEmptyMessage={
                      (balanceSheet?.equity.accounts?.length ?? 0) === 0
                      && balanceSheet?.equity.retainedEarnings !== undefined
                    }
                  />
                  {balanceSheet?.equity.retainedEarnings !== undefined && (
                    <div className="flex justify-between text-sm mt-2 pt-2 border-t border-dashed">
                      <span className="italic text-muted-foreground">{t('retainedEarnings')}</span>
                      <span className="font-mono">
                        {formatCurrency(balanceSheet.equity.retainedEarnings)}
                      </span>
                    </div>
                  )}
                  <div className="mt-4 rounded-lg bg-primary/10 p-3">
                    <div className="flex justify-between font-bold">
                      <span>{t('totalEquity')}</span>
                      <span>{formatCurrency(balanceSheet?.equity.total ?? 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Balance check */}
          {balanceSheet?.totals && (
            <Card
              className={
                balanceSheet.totals.isBalanced
                  ? 'border-success/30'
                  : 'border-destructive/30'
              }
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-lg ${
                        balanceSheet.totals.isBalanced ? 'text-success-text' : 'text-destructive-text'
                      }`}
                    >
                      {balanceSheet.totals.isBalanced ? '✓' : '✗'}
                    </span>
                    <span className="font-medium">
                      {balanceSheet.totals.isBalanced
                        ? t('balanceSheetBalanced')
                        : t('balanceSheetNotBalanced')}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Assets: {formatCurrency(balanceSheet.totals.totalAssets)} | L+E:{' '}
                    {formatCurrency(balanceSheet.totals.totalLiabilitiesAndEquity)}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Balance Sheet Charts */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {assetPieData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('assetsComposition')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={assetPieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                        }
                        labelLine={false}
                      >
                        {assetPieData.map((_, i) => (
                          <Cell key={i} fill={seriesAt(i)} />
                        ))}
                      </Pie>
                      <Tooltip formatter={tooltipFmt} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            {bsCompareData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('assetsVsLiabilities')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={bsCompareData}
                      margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                      <XAxis stroke={axis} dataKey="name" tick={{ fontSize: 12, fill: axis }} />
                      <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} />
                      <Tooltip formatter={tooltipFmt} />
                      <Bar dataKey="value" name={t('amount')} radius={[4, 4, 0, 0]}>
                        <Cell fill={series[0]} />
                        <Cell fill={series[1]} />
                        <Cell fill={series[2]} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ======================= CASH FLOW STATEMENT ======================== */}
      {tab === 'cash-flow' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-muted-foreground">From</Label>
              <Input type="date" value={cfStartDate} onChange={(e) => setCfStartDate(e.target.value)}
                className="h-8 w-auto px-2 py-1" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-muted-foreground">To</Label>
              <Input type="date" value={cfEndDate} onChange={(e) => setCfEndDate(e.target.value)}
                className="h-8 w-auto px-2 py-1" />
            </div>
          </div>

          {cfRangeInvalid ? (
            <p role="alert" data-testid="cf-range-error" className="text-sm text-destructive-text">{t('dateRangeInvalid')}</p>
          ) : cfLoading ? (
            <SkeletonTable rows={8} cols={2} />
          ) : cashFlow ? (
            <>
              {/* Summary KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'Operating Activities', value: cashFlow.operating?.netCashFromOperating ?? 0, color: cashFlow.operating?.netCashFromOperating >= 0 ? 'text-success-text' : 'text-destructive-text' },
                  { label: 'Investing Activities', value: cashFlow.investing?.netCashFromInvesting ?? 0, color: cashFlow.investing?.netCashFromInvesting >= 0 ? 'text-success-text' : 'text-destructive-text' },
                  { label: 'Financing Activities', value: cashFlow.financing?.netCashFromFinancing ?? 0, color: cashFlow.financing?.netCashFromFinancing >= 0 ? 'text-success-text' : 'text-destructive-text' },
                ].map(({ label, value, color }) => (
                  <StatCard
                    key={label}
                    label={label}
                    value={<span className={color}>{formatCurrency(value)}</span>}
                    accent={value >= 0 ? 'success' : 'danger'}
                  />
                ))}
              </div>

              {/* Operating Activities */}
              <Card>
                <CardHeader><CardTitle className="text-base">Operating Activities</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TBody>
                      <Tr hover={false}><Td className="py-2 text-muted-foreground">Net Income</Td><Td className="py-2 text-end font-medium">{formatCurrency(cashFlow.operating?.netIncome ?? 0)}</Td></Tr>
                      <Tr hover={false} className="bg-muted/20"><Td className="py-2 text-xs font-semibold text-muted-foreground uppercase" colSpan={2}>Adjustments for working capital changes</Td></Tr>
                      {Object.entries(cashFlow.operating?.adjustments ?? {}).map(([k, v]) => (
                        <Tr key={k}>
                          <Td className="py-2 text-muted-foreground capitalize">{k.replace(/([A-Z])/g, ' $1')}</Td>
                          <Td className={`py-2 text-end ${Number(v) < 0 ? 'text-destructive-text' : 'text-success-text'}`}>{formatCurrency(Number(v))}</Td>
                        </Tr>
                      ))}
                      <Tr hover={false} className="!border-t-2 bg-muted/30 font-semibold">
                        <Td>Net Cash from Operating Activities</Td>
                        <Td className={`text-end ${cashFlow.operating?.netCashFromOperating >= 0 ? 'text-success-text' : 'text-destructive-text'}`}>{formatCurrency(cashFlow.operating?.netCashFromOperating ?? 0)}</Td>
                      </Tr>
                    </TBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Investing Activities */}
              <Card>
                <CardHeader><CardTitle className="text-base">Investing Activities</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TBody>
                      {(cashFlow.investing?.items ?? []).length === 0
                        ? <Tr hover={false}><Td className="py-4 text-center text-muted-foreground">No investing activities in this period</Td></Tr>
                        : (cashFlow.investing?.items ?? []).map((item: CashFlowItem, i: number) => (
                          <Tr key={i}>
                            <Td className="py-2 text-muted-foreground">{item.description}</Td>
                            <Td className={`py-2 text-end ${item.amount < 0 ? 'text-destructive-text' : 'text-success-text'}`}>{formatCurrency(item.amount)}</Td>
                          </Tr>
                        ))}
                      <Tr hover={false} className="!border-t-2 bg-muted/30 font-semibold">
                        <Td>Net Cash from Investing Activities</Td>
                        <Td className={`text-end ${cashFlow.investing?.netCashFromInvesting >= 0 ? 'text-success-text' : 'text-destructive-text'}`}>{formatCurrency(cashFlow.investing?.netCashFromInvesting ?? 0)}</Td>
                      </Tr>
                    </TBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Financing Activities */}
              <Card>
                <CardHeader><CardTitle className="text-base">Financing Activities</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TBody>
                      {(cashFlow.financing?.items ?? []).length === 0
                        ? <Tr hover={false}><Td className="py-4 text-center text-muted-foreground">No financing activities in this period</Td></Tr>
                        : (cashFlow.financing?.items ?? []).map((item: CashFlowItem, i: number) => (
                          <Tr key={i}>
                            <Td className="py-2 text-muted-foreground">{item.description}</Td>
                            <Td className={`py-2 text-end ${item.amount < 0 ? 'text-destructive-text' : 'text-success-text'}`}>{formatCurrency(item.amount)}</Td>
                          </Tr>
                        ))}
                      <Tr hover={false} className="!border-t-2 bg-muted/30 font-semibold">
                        <Td>Net Cash from Financing Activities</Td>
                        <Td className={`text-end ${cashFlow.financing?.netCashFromFinancing >= 0 ? 'text-success-text' : 'text-destructive-text'}`}>{formatCurrency(cashFlow.financing?.netCashFromFinancing ?? 0)}</Td>
                      </Tr>
                    </TBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Summary */}
              <Card className="border-primary/30">
                <CardContent className="p-0">
                  <Table>
                    <TBody>
                      <Tr hover={false}><Td className="font-medium">Opening Cash Balance</Td><Td className="text-end">{formatCurrency(cashFlow.summary?.openingCash ?? 0)}</Td></Tr>
                      <Tr hover={false}><Td className="font-medium">Net Change in Cash</Td><Td className={`text-end font-medium ${cashFlow.summary?.netChange >= 0 ? 'text-success-text' : 'text-destructive-text'}`}>{formatCurrency(cashFlow.summary?.netChange ?? 0)}</Td></Tr>
                      <Tr hover={false} className="bg-muted/30"><Td className="font-bold text-base">Closing Cash Balance</Td><Td className="text-end font-bold text-base">{formatCurrency(cashFlow.summary?.closingCash ?? 0)}</Td></Tr>
                    </TBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <EmptyState title="No data" subtitle="No posted journal entries found for this period" />
          )}
        </div>
      )}

      {/* ======================= JOURNAL ENTRY DIALOG ======================= */}
      <Dialog
        visible={journalDialogOpen}
        onHide={() => setJournalDialogOpen(false)}
        header={t('newJournalEntry')}
        style={{ width: '48rem' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setJournalDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => handleJournalSave(false)}
              disabled={createJournalEntry.isPending}
            >
              {t('saveAsDraft')}
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-gradient-to-r from-primary via-primary to-primary text-primary-foreground shadow-md shadow-primary/20"
              onClick={() => setConfirmPost(true)}
              disabled={createJournalEntry.isPending}
            >
              {createJournalEntry.isPending ? t('saving') : t('postEntry')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Date & Description */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1 block">
                {t('date')} <span className="text-destructive-text">*</span>
              </Label>
              <Input
                type="date"
                value={journalForm.date}
                onChange={(e) =>
                  setJournalForm({ ...journalForm, date: e.target.value })
                }
              />
            </div>
            <div>
              <Label className="mb-1 block">
                {t('description')} <span className="text-destructive-text">*</span>
              </Label>
              <Input
                type="text"
                value={journalForm.description}
                onChange={(e) =>
                  setJournalForm({ ...journalForm, description: e.target.value })
                }
                placeholder={t('descriptionPlaceholder')}
              />
            </div>
          </div>

          {/* Lines */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>{t('lines')}</Label>
              <button
                type="button"
                onClick={() =>
                  setJournalForm({
                    ...journalForm,
                    lines: [...journalForm.lines, { ...emptyLine }],
                  })
                }
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-primary-text hover:bg-primary/10 dark:hover:bg-primary/10 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('addLine')}
              </button>
            </div>
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-muted-foreground">
                    <th className="px-3 py-2 text-start font-medium">{t('account')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('description')}</th>
                    <th className="px-3 py-2 text-end font-medium w-28">{t('debit')}</th>
                    <th className="px-3 py-2 text-end font-medium w-28">{t('credit')}</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {journalForm.lines.map((line, idx) => (
                    <tr key={idx} className="hover:bg-accent/30 transition-colors">
                      <td className="px-3 py-1.5">
                        <Dropdown
                          value={line.accountId}
                          onChange={(e) => {
                            const lines = [...journalForm.lines];
                            lines[idx] = {
                              ...lines[idx],
                              accountId: e.value as string,
                            };
                            setJournalForm({ ...journalForm, lines });
                          }}
                          options={[
                            { label: t('selectAccount'), value: '' },
                            ...flatAccts.map((acc) => ({
                              label: `${acc.code} - ${acc.name}`,
                              value: acc.id,
                            })),
                          ]}
                          optionLabel="label"
                          optionValue="value"
                          className="p-dropdown-sm w-full min-w-[160px]"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="text"
                          value={line.description}
                          onChange={(e) => {
                            const lines = [...journalForm.lines];
                            lines[idx] = {
                              ...lines[idx],
                              description: e.target.value,
                            };
                            setJournalForm({ ...journalForm, lines });
                          }}
                          placeholder={t('lineNotePlaceholder')}
                          className="h-8 px-2 py-1"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.debitAmount || ''}
                          onChange={(e) => {
                            const lines = [...journalForm.lines];
                            lines[idx] = {
                              ...lines[idx],
                              debitAmount: Number(e.target.value) || 0,
                            };
                            setJournalForm({ ...journalForm, lines });
                          }}
                          className="h-8 px-2 py-1 text-end font-mono"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.creditAmount || ''}
                          onChange={(e) => {
                            const lines = [...journalForm.lines];
                            lines[idx] = {
                              ...lines[idx],
                              creditAmount: Number(e.target.value) || 0,
                            };
                            setJournalForm({ ...journalForm, lines });
                          }}
                          className="h-8 px-2 py-1 text-end font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        {journalForm.lines.length > 2 && (
                          <button
                            type="button"
                            onClick={() => {
                              const lines = journalForm.lines.filter(
                                (_, i) => i !== idx,
                              );
                              setJournalForm({ ...journalForm, lines });
                            }}
                            className="rounded p-1 text-destructive-text hover:bg-destructive/10 dark:hover:bg-destructive/10 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-bold">
                    <td colSpan={2} className="px-3 py-2 text-end">
                      {t('totals')}
                    </td>
                    <td className="px-3 py-2 text-end font-mono">
                      {formatCurrency(
                        journalForm.lines.reduce(
                          (s, l) => s + Number(l.debitAmount),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-2 text-end font-mono">
                      {formatCurrency(
                        journalForm.lines.reduce(
                          (s, l) => s + Number(l.creditAmount),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {(() => {
                        const d = journalForm.lines.reduce(
                          (s, l) => s + Number(l.debitAmount),
                          0,
                        );
                        const c = journalForm.lines.reduce(
                          (s, l) => s + Number(l.creditAmount),
                          0,
                        );
                        const balanced =
                          d > 0 && c > 0 && Math.abs(d - c) < 0.01;
                        return balanced ? (
                          <span className="text-success-text" title={t('balanced')}>
                            ✓
                          </span>
                        ) : (
                          <span className="text-destructive-text" title={t('unbalanced')}>
                            !
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </Dialog>
      <ConfirmDialog
        open={confirmPost}
        onCancel={() => setConfirmPost(false)}
        onConfirm={() => { handleJournalSave(true); setConfirmPost(false); }}
        title={t('postJournalEntry')}
        message={t('postJournalEntryConfirm')}
        confirmLabel={t('postEntry')}
        variant="warning"
      />
      <ConfirmDialog
        open={voidTarget !== null}
        onCancel={() => setVoidTarget(null)}
        onConfirm={() => {
          if (!voidTarget) return;
          if (!voidReason.trim()) { toast.error(t('voidReasonRequired')); return; }
          voidJournalEntry.mutate({ id: voidTarget.id, reason: voidReason.trim() });
        }}
        title={`${t('voidJournalEntry')} ${voidTarget?.entryNumber ?? ''}`}
        message={t('voidJournalEntryConfirm')}
        confirmLabel={t('voidEntry')}
        loading={voidJournalEntry.isPending}
        extra={
          <div className="mt-3 space-y-1">
            {voidTarget && ['StockAdjustment', 'OpeningStock', 'StockCorrection'].includes(voidTarget.referenceType ?? '') && (
              <p className="mb-2 rounded-md bg-muted/50 px-3 py-2 text-sm" data-testid="void-stock-note">{t('voidStockNote')}</p>
            )}
            <Label htmlFor="je-void-reason">{t('voidReason')}</Label>
            <Input
              id="je-void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </div>
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function AccountTreeItem({ account, depth }: { account: AccountNode; depth: number }) {
  return (
    <>
      <div
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50 transition-colors duration-150"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        <span className="font-mono text-xs text-muted-foreground w-12">{account.code}</span>
        <span className="flex-1">{account.name}</span>
        <span className="text-xs text-muted-foreground">{humanizeEnum(account.type)}</span>
      </div>
      {account.children?.map((child) => (
        <AccountTreeItem key={child.id} account={child} depth={depth + 1} />
      ))}
    </>
  );
}

function ReportSection({
  title,
  accounts,
  hideEmptyMessage = false,
}: {
  title: string;
  accounts: ReportAccount[];
  hideEmptyMessage?: boolean;
}) {
  const { t } = useTranslation('finance');
  return (
    <div>
      {title && <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{title}</h3>}
      {accounts.length === 0 ? (
        hideEmptyMessage ? null : <p className="text-xs text-muted-foreground">{t('noData')}</p>
      ) : (
        <div className="space-y-1">
          {accounts.map((a) => (
            <div key={a.accountId} className="flex justify-between text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground">{a.accountCode}</span>
                {a.accountName}
              </span>
              <span className="font-mono">{formatCurrency(a.balance)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
